import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { z } from 'zod';
import { Plus, MoreHorizontal, Loader2, Search, ShieldCheck, ShieldAlert, ShieldOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { PageHeader } from '@/components/layout/page-header';
import { Pagination } from '@/components/data-table/pagination';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';

import { useUsers, useCreateUser, useUpdateUser, useDeleteUser, type User } from '@/hooks/use-users';
import { useMe } from '@/hooks/use-auth';
import { ApiError } from '@/lib/api-client';
import { toast } from '@/hooks/use-toast';
import { roleLabel, relativeTime, shortId, cn } from '@/lib/utils';
import type { Role } from '@weremote/shared';
import { ExportCsvButton } from '@/components/export-csv-button';

export const Route = createFileRoute('/_app/users')({
  component: UsersPage,
});

const ALL_ROLES: Role[] = ['OWNER', 'ADMIN', 'TECHNICIAN', 'VIEWER'];

function UsersPage() {
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState<Role | 'all'>('all');
  const [activeFilter, setActiveFilter] = React.useState<'all' | 'active' | 'inactive'>('all');
  const [editingUser, setEditingUser] = React.useState<User | null>(null);
  const [creating, setCreating] = React.useState(false);

  // Debounce search to avoid flooding the API
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const { data: meData } = useMe();
  const actorRole = meData?.user.role;

  const { data, isLoading, isFetching } = useUsers({
    page,
    pageSize: 20,
    search: debouncedSearch || undefined,
    role: roleFilter === 'all' ? undefined : roleFilter,
    active: activeFilter === 'all' ? undefined : activeFilter === 'active',
  });

  return (
    <>
      <PageHeader
        title="Users"
        description="Manage technicians, admins, and viewers within your organization."
        actions={
          <>
            <ExportCsvButton domain="users" />
            <Button onClick={() => setCreating(true)}>
              <Plus />
              <span>Add user</span>
            </Button>
          </>
        }
      />

      {/* Filters */}
      <Card className="mb-4 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-8"
            />
          </div>

          <Select
            value={roleFilter}
            onValueChange={(v: Role | 'all') => {
              setRoleFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              {ALL_ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  {roleLabel(r)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={activeFilter} onValueChange={(v: 'all' | 'active' | 'inactive') => { setActiveFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last login</TableHead>
              <TableHead>ID</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </TableCell>
              </TableRow>
            ) : data?.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center text-muted-foreground">
                  No users match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              data?.data.map((user) => (
                <TableRow
                  key={user.id}
                  className={cn(isFetching && 'opacity-60 transition-opacity')}
                >
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {user.firstName || user.lastName
                          ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
                          : '—'}
                      </span>
                      <span className="text-xs text-muted-foreground">{user.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <RoleBadge role={user.role} />
                  </TableCell>
                  <TableCell>
                    {user.active ? (
                      <span className="inline-flex items-center gap-1.5 text-success">
                        <span className="status-dot status-dot--online" />
                        <span className="text-xs">Active</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <span className="status-dot status-dot--offline" />
                        <span className="text-xs">Inactive</span>
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground tabular-nums">
                    {relativeTime(user.lastLoginAt)}
                  </TableCell>
                  <TableCell>
                    <span className="id-pill">{shortId(user.id)}</span>
                  </TableCell>
                  <TableCell>
                    <UserRowMenu
                      user={user}
                      onEdit={() => setEditingUser(user)}
                      currentUserId={meData?.user.id}
                      actorRole={actorRole}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {data && (
          <div className="border-t">
            <Pagination
              page={page}
              pageSize={data.pageSize}
              total={data.total}
              onPageChange={setPage}
            />
          </div>
        )}
      </Card>

      <CreateUserDialog open={creating} onOpenChange={setCreating} actorRole={actorRole} />
      <EditUserDialog
        user={editingUser}
        onOpenChange={(open) => !open && setEditingUser(null)}
        actorRole={actorRole}
      />
    </>
  );
}

function RoleBadge({ role }: { role: Role }) {
  const variant = role === 'OWNER' ? 'default' : role === 'ADMIN' ? 'success' : role === 'TECHNICIAN' ? 'secondary' : 'outline';
  return <Badge variant={variant}>{roleLabel(role)}</Badge>;
}

interface UserRowMenuProps {
  user: User;
  onEdit: () => void;
  currentUserId: string | undefined;
  actorRole: Role | undefined;
}

function UserRowMenu({ user, onEdit, currentUserId, actorRole }: UserRowMenuProps) {
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const isSelf = user.id === currentUserId;

  // Disable delete for self and for users above actor's role level
  const ROLE_LEVEL: Record<Role, number> = { VIEWER: 1, TECHNICIAN: 2, ADMIN: 3, OWNER: 4 };
  const cannotModify = !actorRole || ROLE_LEVEL[user.role] > ROLE_LEVEL[actorRole];

  function toggleActive(): void {
    updateUser.mutate(
      { id: user.id, input: { active: !user.active } },
      {
        onSuccess: () => toast({ title: user.active ? 'User deactivated' : 'User activated' }),
        onError: (err) =>
          toast({
            variant: 'destructive',
            title: 'Update failed',
            description: err instanceof ApiError ? err.message : 'Try again',
          }),
      },
    );
  }

  function handleDelete(): void {
    if (!window.confirm(`Delete ${user.email}? This cannot be undone.`)) return;
    deleteUser.mutate(user.id, {
      onSuccess: () => toast({ title: 'User deleted', description: user.email }),
      onError: (err) =>
        toast({
          variant: 'destructive',
          title: 'Delete failed',
          description: err instanceof ApiError ? err.message : 'Try again',
        }),
    });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem onSelect={onEdit} disabled={cannotModify}>
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={toggleActive} disabled={cannotModify || isSelf}>
          {user.active ? <ShieldOff /> : <ShieldCheck />}
          <span>{user.active ? 'Deactivate' : 'Activate'}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={handleDelete} disabled={cannotModify || isSelf}>
          <ShieldAlert />
          <span>Delete</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---- Dialogs ----

const createUserSchema = z.object({
  firstName: z.string().max(100).optional(),
  lastName: z.string().max(100).optional(),
  email: z.string().email(),
  password: z.string().min(12, 'At least 12 characters'),
  role: z.enum(['OWNER', 'ADMIN', 'TECHNICIAN', 'VIEWER']),
});
type CreateUserValues = z.infer<typeof createUserSchema>;

function CreateUserDialog({
  open,
  onOpenChange,
  actorRole,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actorRole: Role | undefined;
}) {
  const createUser = useCreateUser();
  const form = useForm<CreateUserValues>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { role: 'TECHNICIAN' },
  });

  // Only allow creating roles ≤ actor's level
  const ROLE_LEVEL: Record<Role, number> = { VIEWER: 1, TECHNICIAN: 2, ADMIN: 3, OWNER: 4 };
  const availableRoles = ALL_ROLES.filter((r) => !actorRole || ROLE_LEVEL[r] <= ROLE_LEVEL[actorRole]);

  React.useEffect(() => {
    if (open) form.reset({ role: 'TECHNICIAN' });
  }, [open, form]);

  function onSubmit(values: CreateUserValues): void {
    createUser.mutate(values, {
      onSuccess: () => {
        toast({ title: 'User created', description: values.email });
        onOpenChange(false);
      },
      onError: (err) =>
        toast({
          variant: 'destructive',
          title: 'Could not create user',
          description: err instanceof ApiError ? err.message : 'Try again',
        }),
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a new user</DialogTitle>
          <DialogDescription>
            They will be able to sign in immediately with the password you set here.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="firstName">First name</Label>
              <Input id="firstName" {...form.register('firstName')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last name</Label>
              <Input id="lastName" {...form.register('lastName')} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" {...form.register('email')} />
            {form.formState.errors.email && (
              <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Initial password</Label>
            <Input id="password" type="password" autoComplete="new-password" {...form.register('password')} />
            {form.formState.errors.password && (
              <p className="text-xs text-destructive">{form.formState.errors.password.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select
              value={form.watch('role')}
              onValueChange={(v) => form.setValue('role', v as Role)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableRoles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {roleLabel(r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createUser.isPending}>
              {createUser.isPending && <Loader2 className="animate-spin" />}
              Create user
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const editUserSchema = z.object({
  firstName: z.string().max(100).optional(),
  lastName: z.string().max(100).optional(),
  email: z.string().email(),
  role: z.enum(['OWNER', 'ADMIN', 'TECHNICIAN', 'VIEWER']),
  password: z.string().min(12).optional().or(z.literal('')),
});
type EditUserValues = z.infer<typeof editUserSchema>;

function EditUserDialog({
  user,
  onOpenChange,
  actorRole,
}: {
  user: User | null;
  onOpenChange: (open: boolean) => void;
  actorRole: Role | undefined;
}) {
  const updateUser = useUpdateUser();
  const form = useForm<EditUserValues>({ resolver: zodResolver(editUserSchema) });

  const ROLE_LEVEL: Record<Role, number> = { VIEWER: 1, TECHNICIAN: 2, ADMIN: 3, OWNER: 4 };
  const availableRoles = ALL_ROLES.filter((r) => !actorRole || ROLE_LEVEL[r] <= ROLE_LEVEL[actorRole]);

  React.useEffect(() => {
    if (user) {
      form.reset({
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email,
        role: user.role,
        password: '',
      });
    }
  }, [user, form]);

  if (!user) return null;

  function onSubmit(values: EditUserValues): void {
    const input: Record<string, unknown> = {
      firstName: values.firstName || null,
      lastName: values.lastName || null,
      email: values.email,
      role: values.role,
    };
    if (values.password && values.password.length > 0) input.password = values.password;

    updateUser.mutate(
      { id: user!.id, input },
      {
        onSuccess: () => {
          toast({ title: 'User updated' });
          onOpenChange(false);
        },
        onError: (err) =>
          toast({
            variant: 'destructive',
            title: 'Update failed',
            description: err instanceof ApiError ? err.message : 'Try again',
          }),
      },
    );
  }

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>
            <span className="id-pill mt-1 inline-block">{user.id}</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="e-firstName">First name</Label>
              <Input id="e-firstName" {...form.register('firstName')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="e-lastName">Last name</Label>
              <Input id="e-lastName" {...form.register('lastName')} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="e-email">Email</Label>
            <Input id="e-email" type="email" {...form.register('email')} />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={form.watch('role')} onValueChange={(v) => form.setValue('role', v as Role)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableRoles.map((r) => <SelectItem key={r} value={r}>{roleLabel(r)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="e-password">Reset password (optional)</Label>
            <Input id="e-password" type="password" autoComplete="new-password" placeholder="Leave blank to keep" {...form.register('password')} />
            <p className="text-xs text-muted-foreground">
              Changing password will sign the user out of all existing sessions.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateUser.isPending}>
              {updateUser.isPending && <Loader2 className="animate-spin" />}
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
